import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';

import { Store } from '@ngrx/store';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';

import { AdminService } from '../../../admin/services/admin.service';
import { TransactionService } from '../../../home/services/transaction.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { WarningComponent } from '../../../shared/components/warning/warning.component';
import { ConfirmDirective } from '../../../shared/directives/confirm.directive';
import { ButtonDirective } from '../../../ui/button/button/button.directive';

@Component({
  selector: 'semeris-orphan-data-cleaner-modal',
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    LoaderComponent,
    MatCheckboxModule,
    ReactiveFormsModule,
    ConfirmDirective,
    WarningComponent,
    MatSlideToggle,
    ButtonDirective,
  ],
  templateUrl: './orphan-data-cleaner-modal.component.html',
  styleUrls: ['./orphan-data-cleaner-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrphanDataCleanerModalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly adminService = inject(AdminService);
  private readonly transactionService = inject(TransactionService);
  dialogRef = inject<MatDialogRef<OrphanDataCleanerModalComponent>>(MatDialogRef);

  public formGroup = new FormGroup({});
  public transactionIds$: Observable<string[]>;
  public limitReached = false;

  public readonly LIMIT = 100;

  public ngOnInit(): void {
    this.transactionIds$ = this.adminService.getOrphanDeals().pipe(
      take(1),
      map((transactionIds) => {
        const ids = transactionIds.slice(0, this.LIMIT);
        this.limitReached = transactionIds.length > this.LIMIT;
        ids.forEach((transactionId) => {
          this.formGroup.addControl(transactionId, new FormControl<boolean>(false));
        });
        return ids;
      }),
      shareReplay(1)
    );
  }

  public deleteSelected() {
    const transactionIds = Object.keys(this.formGroup.controls || {}).filter(
      (key) => this.formGroup.controls[key].value
    );

    this.transactionService
      .deleteTransactions(transactionIds)
      .pipe(take(1))
      .subscribe(
        () => {
          this.store.dispatch(
            AppActions.PushNotification({
              message:
                'Orphan deals are deleted successfully. Please refresh the page to see the changes.',
              config: {
                panelClass: 'success-notification',
                horizontalPosition: 'center',
                verticalPosition: 'top',
              },
              action: 'Reload application',
              closable: true,
              id: 'reload_application',
            })
          );
          this.dialogRef.close();
        },
        () => {
          this.store.dispatch(
            AppActions.PushNotification({
              message: 'Failed to delete the orphan deals. Please try again.',
              config: 'fail',
            })
          );
        }
      );
  }
}
